import React from 'react';
import { createPortal } from 'react-dom';
import { X, Terminal, Settings, Code, Folder } from 'lucide-react';

/**
 * ProfileDetailsModal - Read-only view of profile properties
 * 
 * Props:
 * - isOpen: boolean - Whether modal is visible
 * - onClose: () => void - Close handler
 * - profile: ProfileData object
 * - launcher: LauncherData object (for context)
 * - machineCapability: Machine capability for this profile (optional, for executable path)
 */
const ProfileDetailsModal = ({ 
  isOpen, 
  onClose, 
  profile, 
  launcher,
  machineCapability = null 
}) => {
  if (!isOpen || !profile) return null;

  // Get executable path from capability if available
  const executablePath = machineCapability?.executable_path || 
    profile.windows_executable || 
    profile.linux_executable || 
    profile.macos_executable ||
    'Not specified';

  // Format environment variables for display
  const envVars = profile.environment_vars || {};
  const hasEnvVars = Object.keys(envVars).length > 0;

  // Format launch arguments
  const launchArgs = profile.launch_args || [];
  const hasLaunchArgs = launchArgs.length > 0;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-white">
              {profile.name}
            </h2>
            <p className="text-sm text-gray-400">
              {launcher?.display_name} Profile
              {profile.version && ` • v${profile.version}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content - scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Executable Path */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Terminal size={16} className="text-blue-400" />
              <h3 className="text-sm font-medium text-gray-300">Executable Path</h3>
            </div>
            <div className="bg-gray-800/50 rounded-lg px-4 py-3 font-mono text-sm text-gray-300 break-all">
              {executablePath}
            </div>
          </div>

          {/* Environment Variables */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Settings size={16} className="text-green-400" />
              <h3 className="text-sm font-medium text-gray-300">Environment Variables</h3>
            </div>
            {hasEnvVars ? (
              <div className="bg-gray-800/50 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left px-4 py-2 text-gray-500 font-medium">Variable</th>
                      <th className="text-left px-4 py-2 text-gray-500 font-medium">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(envVars).map(([key, value]) => (
                      <tr key={key} className="border-b border-gray-700/50 last:border-0">
                        <td className="px-4 py-2 font-mono text-blue-300">{key}</td>
                        <td className="px-4 py-2 font-mono text-gray-300 break-all">{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">No environment variables configured</p>
            )}
          </div>

          {/* Launch Arguments */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Code size={16} className="text-purple-400" />
              <h3 className="text-sm font-medium text-gray-300">Launch Arguments</h3>
            </div>
            {hasLaunchArgs ? (
              <div className="bg-gray-800/50 rounded-lg px-4 py-3">
                <div className="flex flex-wrap gap-2">
                  {launchArgs.map((arg, index) => (
                    <span 
                      key={index}
                      className="px-2 py-1 bg-gray-700 rounded text-sm font-mono text-gray-300"
                    >
                      {arg}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">No launch arguments configured</p>
            )}
          </div>

          {/* Profile Status */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Folder size={16} className="text-amber-400" />
              <h3 className="text-sm font-medium text-gray-300">Status</h3>
            </div>
            <div className="flex items-center gap-4">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm ${
                profile.is_enabled 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-gray-700 text-gray-400'
              }`}>
                <span className={`w-2 h-2 rounded-full ${profile.is_enabled ? 'bg-green-400' : 'bg-gray-500'}`} />
                {profile.is_enabled ? 'Enabled' : 'Disabled'}
              </span>
              {profile.is_default && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm bg-blue-500/20 text-blue-400">
                  Default Profile
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-700">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProfileDetailsModal;

